import React, { useEffect, useState, useMemo } from 'react';
import { Calendar, CheckCircle, RefreshCw, MousePointerClick, MoreHorizontal, ArrowUpDown } from 'lucide-react';
import fetchData from '../api/fetchApi';
import Table from './common/Table';

const StatCard = ({ icon, title, value, delta, iconBgColor }) => (
  <div className="rounded-2xl border border-gray-200 bg-[#fafafa] p-3 md:p-4">
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center justify-center w-9 h-9 rounded-xl text-white"
          style={{ background: iconBgColor }}
        >
          {icon}
        </span>
        <div className="text-sm text-[#1D2035]">{title}</div>
      </div>
      <button className="w-8 h-8 grid place-items-center rounded-lg bg-white text-gray-500">
        <MoreHorizontal size={20} />
      </button>
    </div>
    <div className="mt-3 flex items-end justify-between">
      <div className="text-2xl font-semibold text-[#1D2035]">{value.toLocaleString()}</div>
      <div className="text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
          +{delta}%
        </span>
      </div>
    </div>
  </div>
);

const StatusPill = ({ status }) => {
  const isSuccess = status === "تم الارسال بنجاح";
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border ${
        isSuccess 
        ? "bg-green-50 text-green-700 border-green-200" 
        : "bg-red-50 text-red-700 border-red-200"
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${isSuccess ? "bg-green-500" : "bg-red-500"}`}></span>
      {status}
    </span>
  );
};

export default function NotificationsPage() {
  const dir = "rtl";
  const [dateRange] = useState({ from: "يناير 2025", to: "يونيو 2025" });
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const stats = useMemo(() => [
    { title: "تم إرسالها بنجاح", value: 3056, delta: 4.9, icon: <CheckCircle size={20} />, color: "#059669" },
    { title: "تم فتحها", value: 1535, delta: 3.5, icon: <RefreshCw size={20} />, color: "#2563EB" },
    { title: "تم الضغط", value: 929, delta: 2.3, icon: <MousePointerClick size={20} />, color: "#7C3AED" },
  ], []);

  useEffect(() => {
    async function loadNotifications() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchData(`admin/notifications?page=${page}&per_page=${perPage}`);
        setNotifications(res.items || []);
        setTotal(res.total || 0);
      } catch (e) {
        setError('فشل في جلب الإشعارات');
      } finally {
        setLoading(false);
      }
    }
    loadNotifications();
  }, [page, perPage]);

  const lastPage = total && perPage ? Math.ceil(total / perPage) : 1;

  return (
    <div className="min-h-screen p-4 md:p-6 bg-white" dir={dir}>
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 md:p-6">
        <div className="flex items-center justify-between gap-4">
           <div className="text-right">
            <div className="text-sm text-gray-500">إجمالي الإشعارات المرسلة</div>
            <div className="text-3xl md:text-4xl font-bold text-[#1D2035]">{total.toLocaleString()}</div>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
           
            <button className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-[#1D2035] flex items-center gap-2">
              <ArrowUpDown size={16} className="text-gray-500" />
              من الاحدث الى الاقدم
            </button>
            <button className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-[#1D2035] flex items-center gap-2">
              <Calendar size={16} className="text-gray-500" />
              {dateRange.from} - {dateRange.to}
            </button>
             <button className="px-4 py-2.5 rounded-lg text-white bg-[var(--Primary-Primary)] shadow-sm text-sm md:text-base">
              إرسال إشعار جديد
            </button>
          </div>
         
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mt-4">
          {stats.map((s, idx) => (
            <StatCard key={idx} icon={s.icon} title={s.title} value={s.value} delta={s.delta} iconBgColor={s.color} />
          ))}
        </div>
      </div>

      {/* Table */}
    
        <div className="overflow-x-auto">
          <Table
            columns={[
              { header: 'العنوان', accessor: 'title', className: 'font-semibold text-[#1D2035] max-w-[100px]'  },
              { header: 'المحتوى', accessor: 'message', className: 'text-gray-700 max-w-[200px] truncate' },
              { header: 'تاريخ النشر', accessor: 'created_at', className: 'text-gray-700', render: (r) => {
                  return r.created_at ? r.created_at : '';
              } },
              { header: 'الحالة', accessor: 'seen', className: '', render: (r) => r.seen ? <span className="text-green-600">مقروء</span> : <span className="text-red-600">غير مقروء</span> },
            ]}
            rows={notifications}
            actions={[]}
            act={[]}
            pagination={{
              title: "إشعارات",
              label: `الصفحة ${page} من ${lastPage}`,
              pages: Array.from({ length: lastPage }, (_, i) => i + 1),
              current: page,
              last: lastPage,
              perPage,
              onPageChange: (n) => {
                if (n >= 1 && n <= lastPage) setPage(n);
              },
              onPerPageChange: (n) => {
                setPerPage(n);
                setPage(1);
              },
            }}
            loading={loading}
            error={error}
            noDataMessage="لا توجد إشعارات لعرضها"
          />
        </div>
      </div>
    
    
  );
}
